/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {Box} from 'exsportia-components'
import {useFormikContext} from 'formik'
import React, {FC, memo, useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import {useSubscriptionBooking} from '../../api/subscription-bookings/hooks'
import {useVenue} from '../../api/venue/hooks'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {EmptyList} from '../../components/empty-list/empty-list'
import {SubscriptionPreviewCard} from '../../components/subscription-preview-card/subscription-preview-card'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useAuthState, useHandleLoginState} from '../../services/auth/hooks'
import {hasCycleDueOnCardChange} from '../../services/payments/helpers/checkout-gate'
import {PaymentSubmit, PaymentTypes, usePaymentOptions} from '../../services/payments/hooks/use-payment-options'
import {PaySubscriptionCycleFormType} from '../../services/subscription-bookings/forms/pay-subscription-cycles-form'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {UserPaymentsMethods} from '../set-payment-method/components/user-payments-type-modal'
import {PaySubscriptionBookingInstallmentsOptions} from './components/pay-sub-booking-installment-options'


// TODO: move to separated component file
type Props = {
  paymentType: PaymentTypes
  isLoading: boolean
  onClick: () => void
  disabled?: boolean
}
const SubmitButton: FC<Props> = memo(({
  isLoading,
  onClick,
  disabled = false,
}) => {
  const {l} = useLocale()
  const text = l('save')

  return (
    <Button
      disabled={isLoading || disabled}
      onClick={onClick}
      isLoading={isLoading}
    >
      {text}
    </Button>
  )
})


const Header: FC = memo(() => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('update_saved_card', {caseAction: 'capitalize'})} />
})

export const SubscriptionBookingUpdatePaymentMethodPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {data: venue, isFetching: isVenueLoading} = useVenue()
  const {signUp} = useAuthState()
  const {submitForm, setValues, values, isSubmitting} = useFormikContext<PaySubscriptionCycleFormType>()
  const {data: subscriptionBooking} = useSubscriptionBooking(values.subscriptionBooking?.uid)

  const handleSubmitWithPayment = useCallback(
    (paymentMethodToken: string | null) => {
      setValues({...values, subscriptionBooking: subscriptionBooking || null, paymentMethodToken, paymentType: PaymentTypes.PAY_NOW})
      submitForm().catch(e => console.log(String(e)))
    },
    [values, setValues, submitForm, subscriptionBooking],
  )
  const handleClickButton: PaymentSubmit = useCallback(({
    paymentType,
    paymentMethodToken
  }) => {
    if (paymentType === PaymentTypes.PAY_NOW) {
      return handleSubmitWithPayment(paymentMethodToken)
    }
  }, [submitForm, values, handleSubmitWithPayment])
  const {
    paymentType,
    allowedOptions,
    submitWithPayment,
    paymentMethodToken,
    setUserPaymentToken,
    checkoutGate,
    isCheckingCharge,
  } = usePaymentOptions({
    submit: handleClickButton,
    // @ts-ignore
    entity: subscriptionBooking,
    entityType: 'subscription-booking',
    filterAllowedOptions: [PaymentTypes.PAY_ON_LOCATION, PaymentTypes.ONE_TIME],
    // A plain card swap charges nothing. With a cycle due, the server charges it on save: gate it.
    chargesOnSubmit: hasCycleDueOnCardChange(subscriptionBooking?.cycles),
  })
  const [isClickSubmitting, setIsClickSubmitting] = useState(false)
  const isLoading = isVenueLoading || isSubmitting || isClickSubmitting || isCheckingCharge

  useEffect(() => {
    if (isSubmitting && isClickSubmitting) {
      setIsClickSubmitting(false)
    }
  }, [isSubmitting, isClickSubmitting])

  const handleLogOut = useCallback(() => {
    signUp()
  }, [values])

  useHandleLoginState({
    handleLogOut,
    handleLoggedOut: handleLogOut,
  })

  const navigateBackToProfile = useCallback(() => {
    navigate('../')
  }, [])

  return (
    <GridLayout header={<Header />}>
      {
        (allowedOptions.length === 0)
          ? (
            <EmptyList
              onClick={navigateBackToProfile}
              text={l('byRequestOnly')}
              buttonText={l('back')}
            />
          )
          : (
            <Box>
              <CheckoutGateNotice gate={{...checkoutGate, hasOtherMethod: false}} />
              <Box mb='16px'>
                {
                  allowedOptions.includes(PaymentTypes.PAY_NOW) && (
                    <>
                      <Box>
                        <UserPaymentsMethods
                          isLoading={isLoading}
                          venueId={venue?.uid}
                          paymentMethod={paymentMethodToken}
                          setPaymentMethod={setUserPaymentToken} />
                      </Box>
                    </>
                  )
                }
              </Box>
              <SubmitButton
                isLoading={isLoading}
                paymentType={paymentType}
                disabled={checkoutGate.isSelectionBlocked}
                onClick={() => {
                  if (isLoading) {
                    return
                  }
                  setIsClickSubmitting(true)
                  submitWithPayment()
                }}
              />
            </Box>
          )
      }
      <Box>
        <SubscriptionPreviewCard id={subscriptionBooking?.membershipId} />
        <PaySubscriptionBookingInstallmentsOptions/>
      </Box>
    </GridLayout>
  )
}
